<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { condaApi } from '../condaApi';
import {
  source, items, transcript, selectedConvId, reviewMsgId, convFlags, resolved,
  reviewTurn, chanelMsgs, chanelInput, chanelThreadId,
} from '../state/presidentialState';

// Bot/Chanel emit markdown like the live widget; render it here too.
const mdIt = new MarkdownIt({ breaks: true, linkify: true });
const renderMd = t => DOMPurify.sanitize(mdIt.render(t || ''));

// Assistant name from Personality, used in this page's copy. Falls back to "bot"
// until settings load (or if none is set).
const botName = ref('bot');
condaApi.getSettings().then(s => { if (s?.assistantName) botName.value = s.assistantName; }).catch(() => {});

// Persistent state (source, items, transcript, selectedConvId, reviewMsgId,
// convFlags, resolved, reviewTurn, chanelMsgs, chanelInput, proposal,
// chanelThreadId) lives in ../state/presidentialState so it survives navigating
// away and back. Transient UI flags stay local.
const loadingList = ref(false);
const loadingConv = ref(false);
const showRetrieval = ref(false);
const summary = ref('');          // LLM summary of the open conversation
const summaryLoading = ref(false);
const showSummary = ref(true);
const chanelBusy = ref(false);
const chanelScroll = ref(null); // chat container, kept pinned to the latest message
const chanelBox = ref(null);    // the input textarea, auto-grown to its content

// Retrieval shows only the start of an entry — make that visible.
const snip = (t, n = 160) => { t = t || ''; return t.length > n ? t.slice(0, n) + '…' : t; };

// Grow the Chanel box with what's typed (up to a few lines), shrink when cleared.
watch(chanelInput, () => nextTick(() => {
  const el = chanelBox.value; if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}));

// Friendlier channel names in the UI.
const channelLabel = ch => ({ playground: 'test drive', chatwoot: 'live' }[ch] || ch);

// Customer mood from the judge. Neutral is the norm — only tag the extremes.
const moodTag = s =>
  s === 'frustrated' ? { label: '😤 frustrated', cls: 'red' }
    : s === 'positive' ? { label: '😊 positive', cls: 'green' } : null;

// Lets the operator resolve the turn under review by just typing it in the chat.
const isResolveCmd = t =>
  /^\s*(mark|set)\b[\w\s'’]*\bresolved?\b\s*[.!]?\s*$/i.test(t) ||
  /^\s*resolved?(\s+(this|it|turn))?\s*[.!]?\s*$/i.test(t);

// Keep the Chanel chat scrolled to the bottom as messages (or the typing dots) appear.
watch([() => chanelMsgs.value.length, chanelBusy], () => {
  nextTick(() => { const el = chanelScroll.value; if (el) el.scrollTop = el.scrollHeight; });
});

const REVIEW_PROMPT =
  "In one or two sentences, tell me whether the bot's last answer was good and the main reason — then ask what I'd like to do. Don't list recommendations.";

// ── List ────────────────────────────────────────────────────────────────
async function loadList() {
  loadingList.value = true;
  resolved.value = {}; // server already excludes resolved turns; reset local state
  try {
    items.value = source.value === 'suggested'
      ? await condaApi.insights()
      : await condaApi.conversations();
  } catch (e) {
    items.value = [];
  } finally {
    loadingList.value = false;
  }
}

// Resolve / un-resolve the turn under review. Resolved turns drop out of
// Suggested immediately (and stay out after a refresh — the server filters them).
async function toggleResolve(messageId) {
  if (messageId == null) return;
  const next = !resolved.value[messageId];
  try {
    await condaApi.resolveTurn(messageId, next);
    resolved.value = { ...resolved.value, [messageId]: next };
  } catch (e) {
    chanelMsgs.value.push({ role: 'chanel', content: `⚠ Couldn't update: ${e.message}` });
  }
}

function setSource(s) {
  if (source.value === s) return;
  source.value = s;
  items.value = [];
  // Switching list mode starts fresh: drop the open conversation and Chanel review.
  transcript.value = null;
  selectedConvId.value = null;
  reviewMsgId.value = null;
  convFlags.value = {};
  reviewTurn.value = null;
  chanelMsgs.value = [];
  chanelThreadId.value = null;
  loadList();
}

// One row per conversation: collapse multiple flagged turns from the same chat
// into a single entry (worst verdict + count); every flag stays reachable in the
// transcript, where each flagged turn is tagged and clickable.
const suggestedGroups = computed(() => {
  if (source.value !== 'suggested') return [];
  const map = new Map();
  for (const it of items.value) {
    if (resolved.value[it.botId]) continue; // resolved turns leave the list
    let g = map.get(it.convId);
    if (!g) { g = { convId: it.convId, channel: it.channel, flags: [] }; map.set(it.convId, g); }
    g.flags.push({ botId: it.botId, verdict: it.verdict, reason: it.reason, question: it.question, sentiment: it.sentiment });
  }
  return [...map.values()].map(g => ({
    ...g,
    worst: g.flags.some(f => f.verdict === 'failed') ? 'failed' : 'weak',
    // Frustration anywhere in the chat outranks a calm last turn.
    mood: g.flags.some(f => f.sentiment === 'frustrated') ? 'frustrated'
      : g.flags.some(f => f.sentiment === 'positive') ? 'positive' : null,
  }));
});

function isSelected(idOrConvId) {
  return selectedConvId.value === idOrConvId;
}

// ── Open a conversation into the middle pane ──────────────────────────────
async function openSuggestedGroup(g) {
  const flags = {};
  for (const f of g.flags) flags[f.botId] = { verdict: f.verdict, reason: f.reason };
  // Land on the first failed turn if there is one, else the first flagged turn.
  const primary = g.flags.find(f => f.verdict === 'failed') || g.flags[0];
  await openConversation(g.convId, { flags, flagMsgId: primary.botId });
}
async function openAll(conv) {
  await openConversation(conv.id, {});
}

// Summary loads alongside the transcript (not blocking it); the server caches
// it per conversation, so this is instant after the first time. Guard against
// a slow response landing after the operator opened a different conversation.
async function loadSummary(convId) {
  summary.value = '';
  summaryLoading.value = true;
  try {
    const r = await condaApi.conversationSummary(convId);
    if (selectedConvId.value === convId) summary.value = r.summary || '';
  } catch (e) {
    /* no summary — card just doesn't show */
  } finally {
    if (selectedConvId.value === convId) summaryLoading.value = false;
  }
}

async function openConversation(convId, opts) {
  if (loadingConv.value) return;
  selectedConvId.value = convId;
  loadingConv.value = true;
  transcript.value = null;
  reviewTurn.value = null;
  reviewMsgId.value = null;
  convFlags.value = opts.flags || {};
  resetChanel();
  loadSummary(convId);
  try {
    const conv = await condaApi.conversation(convId);
    transcript.value = conv;
    await nextTick();
    const msgs = conv.messages || [];
    // Auto-review the flagged bot turn (suggested) or the latest bot turn (all).
    let target = null;
    if (opts.flagMsgId != null) target = msgs.find(m => m.id === opts.flagMsgId);
    if (!target) target = [...msgs].reverse().find(m => m.role === 'bot');
    if (target) reviewBotTurn(target, convFlags.value[target.id] || null);
  } catch (e) {
    transcript.value = { error: e.message, messages: [] };
  } finally {
    loadingConv.value = false;
  }
}

// ── Put a bot turn under review (right pane) ──────────────────────────────
function questionFor(msgs, botMsg) {
  const idx = msgs.findIndex(m => m.id === botMsg.id);
  for (let i = idx - 1; i >= 0; i--) if (msgs[i].role === 'user') return msgs[i].content;
  return '';
}

function reviewBotTurn(botMsg, flag) {
  if (!transcript.value) return;
  const msgs = transcript.value.messages || [];
  const ctx = Array.isArray(botMsg.retrieved)
    ? botMsg.retrieved.map(c => ({ ref: c.ref, source: c.source, content: c.content }))
    : [];
  // Everything before this bot turn, so Chanel reads it in context (e.g. a "yea"
  // answering the bot's prior question). The turn's own question stays separate.
  const idx = msgs.findIndex(m => m.id === botMsg.id);
  const history = msgs.slice(0, Math.max(0, idx - 1))
    .map(m => ({ role: m.role === 'bot' ? 'bot' : 'user', content: m.content }));
  reviewMsgId.value = botMsg.id;
  reviewTurn.value = {
    message: questionFor(msgs, botMsg),
    answer: botMsg.content,
    via: botMsg.via,
    confidence: null,
    context: ctx,
    history,
    ...(flag ? { flag } : {}),
  };
  showRetrieval.value = false;
  resetChanel();
  chanelAsk(REVIEW_PROMPT, true);
}

// ── Chanel ────────────────────────────────────────────────────────────────
function resetChanel() {
  chanelMsgs.value = [];
  chanelThreadId.value = null;
}

// What goes back to the model: operator/chanel text turns, plus the OUTCOME of
// each proposal — Chanel doesn't apply changes itself, so without these notes it
// has no idea a change went live and keeps offering to draft it again.
function textHistory() {
  return chanelMsgs.value.flatMap(m => {
    if (m.role === 'operator' || m.role === 'chanel') return [{ role: m.role, content: m.content }];
    if (m.role === 'proposal' && m.status === 'applied')
      return [{ role: 'operator', content: `[applied] I clicked Apply — this change is now LIVE: ${m.summary}` }];
    if (m.role === 'proposal' && m.status === 'dismissed')
      return [{ role: 'operator', content: `[dismissed] I dismissed this proposed change (it was NOT applied): ${m.summary}` }];
    return [];
  });
}

async function chanelAsk(text, hidden = false) {
  if (!reviewTurn.value || chanelBusy.value) return;
  const history = textHistory();
  if (!hidden) chanelMsgs.value.push({ role: 'operator', content: text });
  chanelBusy.value = true;
  await nextTick();
  try {
    const r = await condaApi.chanel({ turn: reviewTurn.value, history, message: text, threadId: chanelThreadId.value, hidden });
    if (r.threadId) chanelThreadId.value = r.threadId;
    chanelMsgs.value.push({ role: 'chanel', content: r.reply });
    if (r.changeReady) await draftInline(); // show the concrete change inline, ready to apply
  } catch (e) {
    chanelMsgs.value.push({ role: 'chanel', content: `⚠ ${e.message}` });
  } finally {
    chanelBusy.value = false;
  }
}

function chanelSend() {
  const t = chanelInput.value.trim();
  if (!t) return;
  // Typing "mark resolved" (etc.) resolves the turn under review — same as the
  // footer button. Only in Suggested mode (resolving is meaningless for All).
  const id = reviewMsgId.value;
  if (id != null && source.value === 'suggested' && isResolveCmd(t)) {
    chanelInput.value = '';
    chanelMsgs.value.push({ role: 'operator', content: t });
    if (resolved.value[id]) {
      chanelMsgs.value.push({ role: 'chanel', content: 'This turn is already resolved.' });
    } else {
      toggleResolve(id); // identical to clicking the Mark resolved button
      chanelMsgs.value.push({ role: 'chanel', content: '✓ Marked resolved — it’ll drop out of Suggested.' });
    }
    return;
  }
  chanelInput.value = '';
  chanelAsk(t);
}

// Highlight just the changed span between two versions, with a little context.
function diffParts(before = '', after = '') {
  before = before || ''; after = after || '';
  let s = 0; const min = Math.min(before.length, after.length);
  while (s < min && before[s] === after[s]) s++;
  let eb = before.length, ea = after.length;
  while (eb > s && ea > s && before[eb - 1] === after[ea - 1]) { eb--; ea--; }
  const CTX = 90;
  const pre = after.slice(0, s), suf = after.slice(ea);
  return {
    prefix: pre.length > CTX ? '…' + pre.slice(-CTX) : pre,
    removed: before.slice(s, eb),
    added: after.slice(s, ea),
    suffix: suf.length > CTX ? suf.slice(0, CTX) + '…' : suf,
  };
}

// When Chanel signals a concrete change, draft it and drop it into the chat
// inline (highlighting only what changed), ready to Apply.
async function draftInline() {
  try {
    const p = await condaApi.propose({ turn: reviewTurn.value, history: textHistory() });
    if (!p.changes || !p.changes.length) return;
    const changes = p.changes.map(c => ({
      ...c,
      _diff: (c.type === 'edit_heart' || c.type === 'edit_entry') ? diffParts(c.before, c.after) : null,
    }));
    chanelMsgs.value.push({ role: 'proposal', summary: p.summary, changes, status: 'pending' });
  } catch (e) {
    chanelMsgs.value.push({ role: 'chanel', content: `⚠ ${e.message}` });
  }
}

// Apply happens ONLY here — Chanel never claims a change is done in prose.
async function applyInline(m) {
  if (m.status !== 'pending') return;
  m.status = 'applying';
  try {
    const clean = m.changes.map(({ _diff, ...c }) => c);
    const { results } = await condaApi.applyChanges(clean, chanelThreadId.value);
    const bad = results.filter(r => !r.ok);
    if (bad.length) { m.status = 'failed'; m.error = bad.map(b => `${b.target}: ${b.error}`).join('; '); return; }
    m.status = 'applied';
    chanelMsgs.value.push({ role: 'chanel', content: `✓ Applied. Try it in Test Drive to see ${botName.value}'s new behaviour.` });
  } catch (e) {
    m.status = 'failed'; m.error = e.message;
  }
}

const changeLabel = c =>
  c.type === 'edit_heart' ? 'Edit Personality'
    : c.type === 'edit_entry' ? `Edit ${c.slug}.${c.field}`
      : c.type === 'add_entry' ? `Add ${c.slug}`
        : c.type === 'merge_entries' ? `Merge → ${c.into}` : c.type;

const shortTime = ts => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

// Load the list only the first time (empty). On return, keep the existing state
// and just re-pin the Chanel chat to its latest message.
onMounted(() => {
  if (!items.value.length) loadList();
  // Summary is local state; on return to an already-open conversation re-fetch
  // it (instant — the server cached it).
  if (selectedConvId.value != null && transcript.value) loadSummary(selectedConvId.value);
  nextTick(() => { if (chanelScroll.value) chanelScroll.value.scrollTop = chanelScroll.value.scrollHeight; });
});
</script>

<template>
  <div class="conda-page">
    <div class="head">
      <div class="row">
        <h1>Review</h1>
        <span class="muted" style="font-size:12px">Real {{ botName }} conversations — inspect them and fix weak answers with Chanel.</span>
      </div>
    </div>

    <div class="split3">
      <!-- ── Left: list ─────────────────────────────────────────────── -->
      <div class="card pane">
        <div class="row between" style="flex:0 0 auto">
          <div class="seg">
            <button class="seg-btn" :class="{ on: source === 'suggested' }" @click="setSource('suggested')">Suggested</button>
            <button class="seg-btn" :class="{ on: source === 'all' }" @click="setSource('all')">All</button>
          </div>
          <button class="btn" :disabled="loadingList" @click="loadList">{{ loadingList ? '…' : 'Refresh' }}</button>
        </div>
        <div class="muted" style="font-size:12px;margin-top:6px;flex:0 0 auto">
          {{ source === 'suggested'
            ? 'Turns Conda flagged as weak or failed. Pick one to see the full chat.'
            : `Every ${botName} conversation. Pick one, then click any ${botName} answer to review it.` }}
        </div>

        <div class="scroll pane-body">
          <div v-if="loadingList" class="empty">Loading…</div>
          <template v-else-if="source === 'suggested'">
            <div v-if="!suggestedGroups.length" class="empty">No flagged turns in the logs yet.</div>
            <div
              v-for="g in suggestedGroups"
              :key="g.convId"
              class="item"
              :class="{ sel: isSelected(g.convId) }"
              @click="openSuggestedGroup(g)"
            >
              <div class="row between">
                <span class="row" style="gap:6px">
                  <span class="tag" :class="g.worst === 'failed' ? 'red' : 'amber'">{{ g.worst }}</span>
                  <span v-if="moodTag(g.mood)" class="tag" :class="moodTag(g.mood).cls">{{ moodTag(g.mood).label }}</span>
                </span>
                <span class="row" style="gap:6px">
                  <span v-if="g.flags.length > 1" class="pill">{{ g.flags.length }} flags</span>
                  <span class="muted" style="font-size:11px">{{ channelLabel(g.channel) }}</span>
                </span>
              </div>
              <div style="margin-top:6px"><strong>{{ g.flags[0].question }}</strong></div>
              <div class="muted reason-clamp" style="margin-top:4px;font-size:12px">{{ g.flags[0].reason }}</div>
              <div v-if="g.flags.length > 1" class="muted" style="margin-top:6px;font-size:11px">
                +{{ g.flags.length - 1 }} more flagged turn{{ g.flags.length - 1 > 1 ? 's' : '' }} in this chat
              </div>
            </div>
          </template>
          <template v-else>
            <div v-if="!items.length" class="empty">No conversations logged yet.</div>
            <div
              v-for="conv in items"
              :key="conv.id"
              class="item"
              :class="{ sel: isSelected(conv.id) }"
              @click="openAll(conv)"
            >
              <div class="row between">
                <span class="pill">{{ channelLabel(conv.channel) }}</span>
                <span class="muted" style="font-size:11px">{{ shortTime(conv.updated_at) }}</span>
              </div>
              <div class="muted reason-clamp" style="margin-top:6px;font-size:13px">{{ conv.preview || '' }}</div>
              <div class="row" style="margin-top:6px;gap:8px">
                <span class="muted" style="font-size:11px">{{ conv.message_count }} msgs</span>
                <span v-if="conv.handed_over" class="tag amber">handover</span>
                <span v-if="moodTag(conv.sentiment)" class="tag" :class="moodTag(conv.sentiment).cls">{{ moodTag(conv.sentiment).label }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- ── Middle: full conversation thread ───────────────────────── -->
      <div class="card pane">
        <div v-if="!transcript && !loadingConv" class="empty" style="margin:auto">
          Pick a conversation on the left to see the whole chat.
        </div>
        <div v-else-if="loadingConv" class="empty" style="margin:auto">Loading conversation…</div>
        <template v-else>
          <div class="row between" style="flex:0 0 auto">
            <strong>Conversation #{{ transcript.id }}</strong>
            <span class="row" style="gap:6px">
              <span class="pill">{{ channelLabel(transcript.channel) }}</span>
              <span v-if="transcript.handed_over" class="tag amber">handover</span>
            </span>
          </div>

          <!-- LLM summary of the whole chat, so the operator gets the gist
               before reading the transcript. -->
          <div v-if="summaryLoading || summary" class="summary-card" style="flex:0 0 auto">
            <div class="row between" style="cursor:pointer" @click="showSummary = !showSummary">
              <span class="muted" style="font-size:11px;letter-spacing:.06em">✨ SUMMARY</span>
              <span class="muted" style="font-size:11px">{{ showSummary ? 'hide' : 'show' }}</span>
            </div>
            <div v-if="showSummary" style="margin-top:6px">
              <div v-if="summaryLoading" class="muted" style="font-size:12px">Summarizing…</div>
              <div v-else class="md summary-md" v-html="renderMd(summary)" />
            </div>
          </div>

          <div class="scroll pane-body chat" style="margin-top:10px">
            <div v-if="transcript.error" class="banner err">{{ transcript.error }}</div>
            <template v-for="m in transcript.messages" :key="m.id">
              <div v-if="m.role === 'user'" class="bubble user">{{ m.content }}</div>
              <div
                v-else
                class="bubble bot turn"
                :class="{ reviewing: reviewMsgId === m.id }"
                @click="reviewBotTurn(m, convFlags[m.id] || null)"
                title="Review this answer with Chanel"
              >
                <span v-if="convFlags[m.id]"
                      class="tag" :class="resolved[m.id] ? 'green' : (convFlags[m.id].verdict === 'failed' ? 'red' : 'amber')"
                      style="margin:0 0 6px">{{ resolved[m.id] ? '✓ resolved' : '⚑ ' + convFlags[m.id].verdict }}</span>
                <div class="md" v-html="renderMd(m.content)" />
                <span class="review-hint">{{ reviewMsgId === m.id ? 'reviewing ▸' : 'review ▸' }}</span>
              </div>
            </template>
          </div>

          <!-- Footer: why flagged + retrieval for the turn under review -->
          <div v-if="reviewTurn" class="turn-foot" style="flex:0 0 auto">
            <div v-if="reviewTurn.flag" class="row between" style="margin-bottom:8px;align-items:flex-start">
              <div class="muted" style="font-size:12px;flex:1 1 auto">
                <span class="tag" :class="resolved[reviewMsgId] ? 'green' : (reviewTurn.flag.verdict === 'failed' ? 'red' : 'amber')">
                  {{ resolved[reviewMsgId] ? '✓ resolved' : '⚑ ' + reviewTurn.flag.verdict }}
                </span>
                {{ reviewTurn.flag.reason }}
              </div>
              <button class="btn" style="flex:0 0 auto" @click="toggleResolve(reviewMsgId)">
                {{ resolved[reviewMsgId] ? '✓ Resolved · Undo' : 'Mark resolved' }}
              </button>
            </div>
            <div class="row between" style="cursor:pointer" @click="showRetrieval = !showRetrieval">
              <span class="muted" style="font-size:12px">
                Retrieval: <span class="pill">{{ reviewTurn.via || '?' }}</span>
                · {{ reviewTurn.context.length }} {{ reviewTurn.context.length === 1 ? 'entry' : 'entries' }}
              </span>
              <span class="muted" style="font-size:12px">{{ showRetrieval ? 'hide' : 'show' }}</span>
            </div>
            <div v-if="showRetrieval" class="list" style="margin-top:8px;max-height:24vh;overflow:auto">
              <div v-if="!reviewTurn.context.length" class="muted" style="font-size:12px">Nothing was retrieved for this turn.</div>
              <div v-for="(c, i) in reviewTurn.context" :key="i" class="item" style="cursor:default;padding:8px 10px">
                <div class="row between"><span><span class="pill">{{ c.source }}</span> <strong>{{ c.ref }}</strong></span></div>
                <div class="muted mono" style="margin-top:4px">{{ snip(c.content) }}</div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- ── Right: Chanel ──────────────────────────────────────────── -->
      <div class="pane" style="display:flex;flex-direction:column;gap:12px;min-height:0;height:100%">
        <div v-if="!reviewTurn" class="card empty" style="margin:auto">
          Select a conversation, then a {{ botName }} answer, and Chanel reviews it here.
        </div>
        <template v-else>
          <div class="card" style="flex:1 1 0%;display:flex;flex-direction:column;min-height:0">
            <div class="row between" style="flex:0 0 auto"><strong>✨ Chanel</strong></div>
            <div ref="chanelScroll" class="scroll chat" style="flex:1 1 auto;min-height:0;margin-top:10px">
              <template v-for="(m, i) in chanelMsgs" :key="i">
                <div v-if="m.role === 'operator'" class="bubble user">{{ m.content }}</div>
                <div v-else-if="m.role === 'chanel'" class="bubble bot"><div class="md" v-html="renderMd(m.content)" /></div>
                <div v-else-if="m.role === 'proposal'" class="bubble bot proposal">
                  <div class="muted" style="font-size:12px;margin-bottom:8px">{{ m.summary }}</div>
                  <div v-for="(c, ci) in m.changes" :key="ci" style="margin-bottom:8px">
                    <span class="tag amber">{{ changeLabel(c) }}</span>
                    <div v-if="c._diff" class="diff mono"><span class="diff-ctx">{{ c._diff.prefix }}</span><span v-if="c._diff.removed" class="diff-del">{{ c._diff.removed }}</span><span class="diff-add">{{ c._diff.added }}</span><span class="diff-ctx">{{ c._diff.suffix }}</span></div>
                    <div v-else-if="c.type === 'add_entry'" class="diff mono">
                      <div><strong>{{ c.slug }}</strong> <span class="muted">{{ c.category }}</span></div>
                      <div class="muted" style="margin:2px 0 6px">{{ c.description }}</div>
                      <span class="diff-add">{{ c.response }}</span>
                    </div>
                    <div v-else-if="c.type === 'merge_entries'" class="diff mono">
                      Keep <strong>{{ c.into }}</strong>, remove {{ c.slugs.filter(s => s !== c.into).join(', ') }}
                      <div style="margin-top:6px"><span class="diff-add">{{ c.response }}</span></div>
                    </div>
                  </div>
                  <div v-if="m.status === 'pending'" class="row" style="gap:8px;margin-top:6px">
                    <button class="btn primary" @click="applyInline(m)">Apply this change</button>
                    <button class="btn" @click="m.status = 'dismissed'">Dismiss</button>
                  </div>
                  <div v-else-if="m.status === 'applying'" class="muted" style="margin-top:6px;font-size:12px">Applying…</div>
                  <div v-else-if="m.status === 'applied'" style="margin-top:6px"><span class="tag green">✓ Applied</span></div>
                  <div v-else-if="m.status === 'dismissed'" class="muted" style="margin-top:6px;font-size:12px">Dismissed</div>
                  <div v-else-if="m.status === 'failed'" class="muted" style="margin-top:6px;font-size:12px;color:#f6a6a6">⚠ {{ m.error }}</div>
                </div>
              </template>
              <div v-if="chanelBusy" class="bubble bot muted">…</div>
            </div>
            <div class="row" style="margin-top:12px;flex:0 0 auto;align-items:flex-end">
              <textarea
                ref="chanelBox"
                v-model="chanelInput"
                class="in chanel-in"
                rows="1"
                :placeholder="source === 'suggested' && reviewMsgId != null ? 'Ask Chanel… or type “mark resolved”' : 'Ask Chanel…'"
                @keydown.enter.exact.prevent="chanelSend"
              />
              <button class="btn primary" :disabled="chanelBusy" @click="chanelSend">Send</button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped src="./conda.css"></style>

<style scoped>
.split3 {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr) 380px;
  grid-template-rows: minmax(0, 1fr); /* fill the row height, don't size to content */
  gap: 14px;
  height: calc(100vh - 130px);
}
.pane { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
.pane-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; margin-top: 10px; }
.seg { display: inline-flex; background: #171a21; border: 1px solid #2a2f3a; border-radius: 8px; padding: 2px; }
.seg-btn { background: transparent; border: none; color: #9aa1ad; padding: 5px 12px; border-radius: 6px; cursor: pointer; font-size: 12px; }
.seg-btn.on { background: #4f46e5; color: #fff; }
.bubble.turn { cursor: pointer; position: relative; padding-bottom: 18px; }
.bubble.turn:hover { border-color: #6366f1; }
.bubble.turn.reviewing { border-color: #6366f1; box-shadow: 0 0 0 1px #6366f1 inset; }
.bubble.turn .review-hint { position: absolute; right: 10px; bottom: 5px; font-size: 10px; color: #6b7280; }
.bubble.turn:hover .review-hint, .bubble.turn.reviewing .review-hint { color: #a5b4fc; }
.turn-foot { border-top: 1px solid #2a2f3a; padding-top: 10px; margin-top: 8px; }
.summary-card { background: #15192a; border: 1px solid #2e3450; border-radius: 10px; padding: 10px 12px; margin-top: 10px; }
.summary-md { font-size: 13px; }
/* List rows stay tidy; the full reason shows in the footer of the open turn. */
.reason-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}
</style>

<!-- Unscoped: scoped styles don't reach v-html. Contained under .conda-page .md -->
<style>
.conda-page .md { white-space: normal; line-height: 1.5; }
.conda-page .md > :first-child { margin-top: 0; }
.conda-page .md > :last-child { margin-bottom: 0; }
.conda-page .md p { margin: 0 0 8px; }
.conda-page .md ul, .conda-page .md ol { margin: 6px 0; padding-left: 20px; }
.conda-page .md li { margin: 3px 0; }
.conda-page .md strong { font-weight: 700; }
.conda-page .md a { color: #a5b4fc; text-decoration: underline; }
.conda-page .md code { background: rgba(255,255,255,.10); padding: 1px 5px; border-radius: 5px; font-family: ui-monospace, Menlo, monospace; font-size: 12px; }
</style>
